import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Page,
  PageHeader,
  SearchSelect,
} from '@evogard/ui';
import {
  SERVICE_VARIANTS,
  SERVICE_VARIANT_LABELS,
  type AssistanceServiceType,
  type ServiceVariant,
} from '@evogard/domain';
import type { AssistanceCatalogEntry } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { ASSISTANCE_KEY } from './assistance.js';
import { useAssistanceCatalog } from './catalog.js';

/** Quantos prestadores atendem, numa frase. */
function providersText(n: number): string {
  if (n === 0) return 'nenhum prestador';
  return n === 1 ? '1 prestador' : `${n} prestadores`;
}

/**
 * Plataforma › Assistência 24h › Serviços (02/11/2026): o catálogo da base.
 *
 * O TIPO (reboque, bateria…) é código: é dele a cota, a categoria do Conta
 * Azul, o motivo e o itinerário, e por isso esta tela não cria tipo. O
 * SERVIÇO nasce aqui, dentro de um tipo, e herda tudo dele — é ele que o
 * prestador marca como atendido, com o seu preço. Arquivar não apaga: o
 * serviço some do acionamento e do cadastro do prestador, e continua nos
 * acionamentos que o usaram.
 */
export function ServicesScreen() {
  const client = useQueryClient();
  const catalog = useAssistanceCatalog();
  const [group, setGroup] = useState<AssistanceServiceType | ''>('');
  const [name, setName] = useState('');
  const [variant, setVariant] = useState<ServiceVariant | ''>('');
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const residential = group === 'residencial';
  const refresh = () => void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });

  const create = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistance.createService({
          body: {
            serviceType: group as AssistanceServiceType,
            name: name.trim(),
            fixedVariant: residential ? variant || null : null,
          },
        }),
        201,
        'Não foi possível criar o serviço.',
      ),
    onSuccess: () => {
      setName('');
      setVariant('');
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: async (input: { id: number; name?: string; archived?: boolean }) =>
      ok(
        await api.assistance.updateService({
          params: { id: input.id },
          body: { name: input.name, archived: input.archived },
        }),
        200,
        'Não foi possível mudar o serviço.',
      ),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
  });
  const canCreate =
    group !== '' && name.trim().length >= 3 && (!residential || variant !== '');

  return (
    <Page measure="cheia">
      <PageHeader title="Serviços da assistência">
        O que se aciona e se cobra, dentro de cada tipo. O tipo decide o itinerário, o
        motivo, a cota e a categoria do Conta Azul; o serviço decide o preço de cada
        prestador.
      </PageHeader>
      {(catalog.error ?? update.error) && (
        <Alert tone="erro">{(catalog.error ?? update.error)!.message}</Alert>
      )}

      <Card title="Novo serviço">
        <div className="grid items-end gap-3 md:grid-cols-[14rem_minmax(0,1fr)_14rem_auto]">
          <SearchSelect
            label="Tipo"
            placeholder="Escolha…"
            options={(catalog.data ?? []).map((g) => ({
              value: g.serviceType,
              label: g.name,
            }))}
            value={group}
            onChange={(v) => {
              setGroup(v as AssistanceServiceType | '');
              setVariant('');
            }}
          />
          <Field
            label="Nome do serviço"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
          {residential ? (
            <SearchSelect
              label="Categoria"
              placeholder="Escolha…"
              options={SERVICE_VARIANTS.residencial.map((v) => ({
                value: v,
                label: SERVICE_VARIANT_LABELS[v],
              }))}
              value={variant}
              onChange={(v) => setVariant(v as ServiceVariant | '')}
            />
          ) : (
            <span />
          )}
          <Button
            icon="mais"
            disabled={!canCreate}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Criar serviço
          </Button>
        </div>
        {residential && (
          <p className="mt-2 text-xs text-texto-tenue">
            No residencial a categoria do Conta Azul vem do serviço: escolha qual delas
            ele usa.
          </p>
        )}
        {create.error && (
          <div className="mt-3">
            <Alert tone="erro">{create.error.message}</Alert>
          </div>
        )}
      </Card>

      {catalog.data?.map((g) => (
        <Card
          key={g.serviceType}
          title={g.name}
          descricao={g.needsDestination ? 'Com destino' : 'Sem destino'}
        >
          <ul className="flex flex-col divide-y divide-borda">
            {g.services.map((s: AssistanceCatalogEntry) => {
              const archived = s.archivedAt !== null;
              const editing = renaming?.id === s.id;
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    {editing ? (
                      <Field
                        label="Novo nome"
                        value={renaming.name}
                        maxLength={80}
                        onChange={(e) => setRenaming({ id: s.id, name: e.target.value })}
                      />
                    ) : (
                      <>
                        <b className={archived ? 'text-texto-suave' : undefined}>
                          {s.name}
                        </b>
                        <span className="block text-xs text-texto-tenue">
                          {s.code} · {providersText(s.providerCount)}
                          {s.fixedVariant
                            ? ` · ${SERVICE_VARIANT_LABELS[s.fixedVariant as ServiceVariant]}`
                            : ''}
                        </span>
                      </>
                    )}
                  </div>
                  <Badge tone={archived ? 'neutro' : 'sucesso'}>
                    {archived ? 'Arquivado' : 'Ativo'}
                  </Badge>
                  {editing ? (
                    <>
                      <Button
                        size="sm"
                        disabled={renaming.name.trim().length < 3}
                        loading={update.isPending}
                        onClick={() =>
                          update.mutate({ id: s.id, name: renaming.name.trim() })
                        }
                      >
                        Gravar nome
                      </Button>
                      <Button
                        size="sm"
                        variant="discreto"
                        onClick={() => setRenaming(null)}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="discreto"
                        onClick={() => setRenaming({ id: s.id, name: s.name })}
                      >
                        Renomear
                      </Button>
                      <Button
                        size="sm"
                        variant="discreto"
                        loading={update.isPending && update.variables?.id === s.id}
                        onClick={() => update.mutate({ id: s.id, archived: !archived })}
                      >
                        {archived ? 'Reativar' : 'Arquivar'}
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
    </Page>
  );
}
